<template>
    <div class="row">
        <div class="col-12 col-lg-7 d-flex flex-column">
            <div class="animated fadeInUp faster">
                <div class="card">
                    <form @submit.prevent="onFormSubmit">
                        <div class="card-header">
                            <div class="row align-items-center">
                                <div class="col-auto"><i class="fal fa-user-circle text-primary fa-2x"></i></div>
                                <div class="col pl-0"><h5 class="mb-0 font-weight-normal">{{ $t('details.header') }}</h5></div>
                            </div>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-12 col-md-6">
                                    <div class="form-group">
                                        <label>{{ $t("details.form.first_name") }}</label>
                                        <input type="text" :placeholder="$t(`details.form.first_name_placeholder`)" autocomplete="given-name" class="form-control" v-model="form.first_name" />
                                    </div>
                                </div>
                                <div class="col-12 col-md-6">
                                    <div class="form-group">
                                        <label>{{ $t("details.form.last_name") }}</label>
                                        <input type="text" :placeholder="$t(`details.form.last_name_placeholder`)" autocomplete="family-name" class="form-control" v-model="form.last_name" />
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <label>{{ $t("details.form.email_address") }}</label>
                                <div class="input-group">
                                    <div class="input-group-prepend">
                                        <span class="input-group-text" id="email-addon"><i class="fal fa-envelope text-primary"></i></span>
                                    </div>
                                    <input type="email" class="form-control" :placeholder="$t(`details.form.email_address_placeholder`)" autocomplete="email" v-model="form.email">
                                </div>
                            </div>
                            <div class="row">
                                <div v-bind:class="this.needsPostalCode ? 'col-12 col-md-6' : 'col-12'">
                                    <div class="form-group mb-md-0">
                                        <label>{{ $t("details.form.country") }}</label>
                                        <div class="input-group">
                                            <div class="input-group-prepend">
                                                <span class="input-group-text" id="country-addon"><i class="fal text-primary" :class="{ 'fa-globe': !this.countryLoading, 'fa-spin fa-spinner': this.countryLoading }"></i></span>
                                            </div>
                                            <v-select v-if="country == 'XX'" id='basket-country' class="form-control" append-to-body v-model="country" :options="countries" :reduce="country => country.code" label="name" />
                                            <span class="form-control" v-if="country != 'XX'">{{ countryName }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-12 col-md-6"  v-if="this.needsPostalCode">
                                    <div class="form-group mb-0">
                                        <label>{{ $t("details.form.postal_code") }}</label>
                                        <div class="input-group">
                                            <input type="text" class="form-control" :placeholder="$t(`details.form.postal_code`)" autocomplete="postalcode" v-model="form.postal_code">
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="border-top card-body">
                            <div class="row">
                                <div class="col-12">
                                    <div class="custom-control custom-checkbox">
                                        <input type="checkbox" class="custom-control-input" id="gdpr-consent" v-model="gdprConsent">
                                        <i18n path="payment_method.marketing_consent" class="custom-control-label text-muted" for="gdpr-consent" tag="label">
                                            <template v-slot:storeName>{{ store.name }}</template>
                                        </i18n>
                                    </div>
                                </div>
                            </div>
                            <div class="row mt-4">
                                <div class="col-12">
                                    <div class="custom-control custom-checkbox mb-0">
                                        <input type="checkbox" class="custom-control-input" id="privacy-agreement" required v-model="privacyTermsAccepted">


                                        <i18n v-if="store.additionalTerms.length" path="details.privacy_terms_additional" class="custom-control-label text-muted" for="privacy-agreement" tag="label">
                                            <template v-slot:privacyLink>
                                                <a href="/privacy" target="_blank" class="font-weight-bold">{{ $t('details.privacy_policy_header') }}</a>
                                            </template>
                                            <template v-slot:termsLink>
                                                <a href="/terms" target="_blank" class="font-weight-bold">{{ $t('details.terms_header') }}</a>
                                            </template>
                                            <template v-slot:additional>
                                                <span v-for="(additionalTerm, index) in store.additionalTerms"  target="_blank" class="font-weight-bold">
                                                    <a v-if="index === store.additionalTerms.length - 1" :href="additionalTerm.url" target="_blank">{{ additionalTerm.name }}</a>
                                                    <span v-else-if="index === store.additionalTerms.length - 2">
                                                        <a :href="additionalTerm.url" target="_blank">{{ additionalTerm.name }}</a>
                                                        <span class="text-decoration-none font-weight-light"> and </span>
                                                    </span>
                                                    <span v-else>
                                                        <a :href="additionalTerm.url" target="_blank">{{ additionalTerm.name }}</a>,
                                                    </span>
                                                </span>
                                            </template>
                                        </i18n>

                                        <i18n v-else path="details.privacy_terms" class="custom-control-label text-muted" for="privacy-agreement" tag="label">
                                            <template v-slot:privacyLink>
                                                <a href="/privacy" target="_blank" class="font-weight-bold">{{ $t('details.privacy_policy_header') }}</a>
                                            </template>
                                            <template v-slot:termsLink>
                                                <a href="/terms" target="_blank" class="font-weight-bold">{{ $t('details.terms_header') }}</a>
                                            </template>
                                        </i18n>

                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="card-footer">
                            <div class="row align-items-center">
                                <div class="col-md-6 col-lg-6 mb-4 mb-md-0 mr-md-0">
                                    <a :href="store.basketUrl" class="btn btn-text d-block d-md-inline-block px-0"><i class="fas fa-angle-left mr-3"></i>{{ $t("details.back_to_basket") }}</a>
                                </div>
                                <div class="col-md-6 col-lg-6 text-md-right">
                                    <button type="submit" class="btn btn-primary btn-block" :disabled="loading || countryLoading">
                                        {{ $t("details.continue_to_payment") }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
                <div class="card mt-4 mb-4">
                    <div class="card-body">
                        <div class="row align-items-center justify-content-center">
                            <div class="col-md-auto text-center text-md-left mb-3 mb-md-0 px-lg-0 px-xl-3">
                                <i class="fas fa-lock-alt mr-2"></i>
                                <span>{{ $t("details.secured_by") }}</span>
                            </div>
                            <div class="col-auto text-center text-md-left mb-3 mb-md-0">
                                <img class="img-fluid mr-3" :src="darkMode ? '/img/trust/visa-light.svg' : '/img/trust/visa.svg'"  />
                            </div>
                            <div class="col-auto text-center text-md-left mb-3 mb-md-0">
                                <img class="img-fluid mr-3" :src="darkMode ? '/img/trust/mastercard-light.svg' : '/img/trust/mastercard.svg'"  />
                            </div>
                            <div class="col-auto text-center text-md-left">
                                <img class="img-fluid" style="max-height:25px" :src="darkMode ? '/img/trust/paypal-light.svg' : '/img/trust/paypal.svg'"  />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="d-flex flex-column col-12 col-lg-5">
            <BasketList />
            <CreatorCode v-if="store.hasCreatorCodes" />
            <Coupon />
        </div>
    </div>
</template>

<script>

    import {mapActions, mapMutations, mapState} from "vuex";
    import { getData as getCountries, getName as getCountryByCode } from "country-list";
    import BasketList from "./BasketList";
    import Coupon from "./Coupon";
    import CreatorCode from "./CreatorCode"
    import {postcodeValidator} from "postcode-validator";

    export default {
        components: {Coupon, BasketList, CreatorCode},
        data() {
            return {
                form: {},
                countries: getCountries(),
                loading: false,
                countryLoading: false,
                postalCountries: [
                    'US', 'GB', 'CA', 'AU', 'SG', 'TR', 'NZ', 'IS', 'CH', 'NO', 'ZA', 'SA', 'KR', 'CL', 'TW'
                ],
                privacyTermsAccepted: false
            }
        },
        computed : {
            ...mapState({
                darkMode: state => state.app.store.darkMode,
                store: state => state.app.store,
                customer: state => state.customer,
                basket: state => state.basket
            }),
            gdprConsent:  {
                get: function() {
                    return this.$store.state.payment.gdprConsent;
                },
                set: function(value) {
                    this.setGdprConsent(value);
                }
            },
            country : {
                get: function() {
                    return this.$store.state.customer.country;
                },
                set: function(value) {
                    if (!getCountryByCode(value)) {
                        return this.$swal({type: 'error', text: this.$t("details.messages.country_not_provided")});
                    }
                    this.countryLoading = true;
                    this.updateCountry(value).catch((error) => {
                        this.$swal({ type: 'error', text: error.response.data.message });
                    }).finally(() => {
                        this.countryLoading = false;
                    });
                }
            },
            countryName : function () {
                if (this.country == 'GB') {
                    return 'United Kingdom';
                }
                return getCountryByCode(this.country)
            },
            needsPostalCode: function(){
                return this.postalCountries.includes(this.country);
            }
        },
        mounted() {
            this.form = Object.assign({}, this.customer);
            window.dataLayer2.push({ ecommerce: null });
            window.dataLayer2.push({
                event: "begin_checkout",
                currency: this.store.currency,
                value: this.basket.price.balance
            });
        },
        methods: {
            onFormSubmit: function() {
                if (!this.privacyTermsAccepted) {
                    return this.$swal({ type: 'error', text: this.$t('payment_method.messages.privacy_terms_not_accepted') });
                }

                if (this.form.first_name === '' || this.form.last_name === '' || this.form.email === '' ) {
                    return this.$swal({ type: 'error', text: this.$t("details.messages.form_not_complete") });
                }

                if (!getCountryByCode(this.country)) {
                    return this.$swal({ type: 'error', text: this.$t("details.messages.country_not_provided") });
                }

                if (this.needsPostalCode && !postcodeValidator(this.form.postal_code, this.country)) {
                    return this.$swal({ type: 'error', text: this.$t("details.messages.postcode_not_provided") });
                }

                this.loading = true;

                this.updateCustomer(this.form).then(() => {
                    this.nextStep();
                }).catch((err) => {
                    this.$swal({ type : 'error', text : err.response.data.message });
                }).finally(() => {
                    this.loading = false;
                })
            },
            ...mapActions(['updateCustomer', 'updateCountry']),
            ...mapMutations(['nextStep', 'setGdprConsent'])
        },
        name: "Details"
    }
</script>

<style scoped>

</style>
